<template>
  <div class="emh-side-filters-categories-toggle">
    <h4 v-if="titleTranslationExists" class="emh-side-filters-categories-toggle__title wb-type-copy-strong">
      {{ $t('dcp.sideFilters.categoriesToggle.title') }}
    </h4>

    <ui-button-group
      v-if="isMobile"
      class="emh-side-filters-categories-toggle__button-group"
      group-name="category-options"
      :disabled="isLoading"
      :options="buttonGroupOptions"
      data-test-id="emh-side-filters-categories-toggle__button-group"
      @change-option="onChangeCategory"
    />
    <ui-radio-button-group
      v-else
      class="emh-side-filters-categories-toggle__button-group"
      group-name="category-options"
      :disabled="isLoading"
      :options="buttonGroupOptions"
      data-test-id="emh-side-filters-categories-toggle__button-group"
      @change-option="onChangeCategory"
    />

    <p v-if="hintTranslationExists" class="emh-side-filters-categories-toggle__hint wb-type-hint">
      {{ $t('dcp.sideFilters.categoriesToggle.hint') }}
    </p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs } from 'vue';
import UiButtonGroup from '@dh-io-emh/ui/src/form/buttonGroup/ButtonGroup.vue';
import UiRadioButtonGroup from '@dh-io-emh/ui/src/form/radioButtonGroup/RadioButtonGroup.vue';
import { ButtonGroupOption } from '@dh-io-emh/ui/src/form/buttonGroup/button-group.model';
import { FilterCategory } from '@dh-io-emh/config-types';
import { useI18n } from '@dh-io-emh/shared/src/composables/i18n.composable';
import { viewPortService } from '@dh-io-emh/ui/src/utils/viewport.util';

export default defineComponent({
  name: 'EmhSideFiltersCategoriesToggle',
  components: { UiButtonGroup, UiRadioButtonGroup },

  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
    availableCategories: {
      type: Array as PropType<FilterCategory[]>,
      default: () => [],
    },
    selectedCategory: {
      type: String,
      required: true,
    },
  },

  emits: ['change-category'],

  setup(props, { emit }) {
    const { availableCategories, selectedCategory, isLoading } = toRefs(props);
    const { t, exists } = useI18n();
    const isMobile = ref<boolean>(viewPortService.isMobile());

    const buttonGroupOptions = computed(() =>
      availableCategories.value?.map(
        ({ name }): ButtonGroupOption => ({
          label: t(`dcp.srp.filters.${name}.headline`),
          key: name,
          value: name,
          selected: name === selectedCategory.value,
          disabled: isLoading.value,
        }),
      ),
    );

    const onChangeCategory = ({ value }: ButtonGroupOption) => emit('change-category', value);

    const titleTranslationExists = computed(() => exists('dcp.sideFilters.categoriesToggle.title'));
    const hintTranslationExists = computed(() => exists('dcp.sideFilters.categoriesToggle.hint'));

    return {
      buttonGroupOptions,
      isMobile,
      titleTranslationExists,
      hintTranslationExists,
      onChangeCategory,
    };
  },
});
</script>

<style lang="scss" scoped>
.emh-side-filters-categories-toggle {
  &__title,
  &__button-group {
    width: 100%;
  }

  &__title {
    @include wb-spacing('margin-bottom', 'xs');
  }

  &__hint {
    @include wb-spacing('margin-top', 'xs');
    color: var(--wb-grey-45);
  }
}
</style>
